import {useState} from 'react'
import {PlusIcon, RepoForkedIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, ButtonGroup, CounterLabel, IconButton, Spinner, Tooltip} from '@primer/react'
import {Count} from '@github-ui/count'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useViewerForksQuery} from './hooks/use-viewer-forks-query'
import type {ForkabilityError, ViewerFork} from './types'

export interface ForkButtonProps {
  /** Total number of forks for the repository */
  forkCount: number
  /** Whether the current user has permission to fork */
  canFork: boolean
  /** Server-provided reason forking is blocked — mapped to a user-facing message */
  forkabilityError?: ForkabilityError | null
  /** URL path for the fork creation page */
  forkSelectPath: string
  /** URL path to fetch the viewer's forks on demand (my_forks_menu_content) */
  forksApiPath: string
  /** Whether the current user is logged in */
  isLoggedIn: boolean
  /** Full repository name with owner, e.g. "octocat/hello-world" */
  repoNameWithOwner: string
  /**
   * Button size
   * @default "small"
   */
  size?: 'small' | 'medium'
}

/** Maps server forkability error enums to user-facing tooltip messages */
function getForkabilityMessage(error: ForkabilityError, repoNameWithOwner: string): string {
  switch (error) {
    case 'forking_disabled':
      return `Cannot fork because ${repoNameWithOwner} has forking disabled`
    case 'repo_locked':
      return `Cannot fork because ${repoNameWithOwner} is locked`
    case 'repo_disabled':
      return `Cannot fork because ${repoNameWithOwner} is disabled`
    case 'dmca':
      return `Cannot fork because ${repoNameWithOwner} is unavailable due to a DMCA takedown`
    case 'repo_offline':
      return 'Cannot fork because repository is offline'
    case 'unavailable':
      return `Cannot fork because ${repoNameWithOwner} is currently unavailable`
    case 'nowhere_to_fork':
      return `You own ${repoNameWithOwner} and are not a member of any organizations`
    case 'repo_empty':
      return `Cannot fork because ${repoNameWithOwner} is empty`
    case 'emu_blocked':
      return 'Cannot fork because the repository is outside of your enterprise'
    default:
      return `Cannot fork ${repoNameWithOwner}`
  }
}

/**
 * Fork button with counter and existing forks dropdown for the repository header.
 *
 * Always renders as a split button:
 * - Left: "Fork" + count — links to fork creation page (or login if signed out)
 * - Right: dropdown caret — opens lazy-loaded list of viewer's existing forks
 */
export function ForkButton({
  forkCount,
  canFork,
  forkabilityError,
  forkSelectPath,
  forksApiPath,
  isLoggedIn,
  repoNameWithOwner,
  size = 'small',
}: ForkButtonProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const {forks, isLoading, isError} = useViewerForksQuery({
    forksApiPath,
    enabled: dropdownOpen,
  })

  const loginReturnPath = `/${repoNameWithOwner}`
  const primaryHref = isLoggedIn ? forkSelectPath : `/login?return_to=${encodeURIComponent(loginReturnPath)}`

  if (!isLoggedIn) {
    return (
      <Button
        as="a"
        href={primaryHref}
        size={size}
        leadingVisual={RepoForkedIcon}
        data-testid="fork-button"
        aria-label="You must be signed in to fork a repository"
        onClick={() =>
          sendClickAnalyticsEvent({
            category: 'REPOSITORY_OVERVIEW',
            action: 'click:fork_login',
            label: 'location:header;ref_cta:fork_button',
          })
        }
      >
        Fork
        <CounterLabel className="ml-1">
          <Count value={forkCount} />
        </CounterLabel>
      </Button>
    )
  }

  if (!canFork) {
    const tooltipText = forkabilityError
      ? getForkabilityMessage(forkabilityError, repoNameWithOwner)
      : `Cannot fork ${repoNameWithOwner}`
    return (
      <Tooltip text={tooltipText} type="description" direction="s">
        <Button size={size} leadingVisual={RepoForkedIcon} inactive data-testid="fork-button" aria-label={tooltipText}>
          Fork
          <CounterLabel className="ml-1">
            <Count value={forkCount} />
          </CounterLabel>
        </Button>
      </Tooltip>
    )
  }

  return (
    <ButtonGroup>
      <Button
        as="a"
        href={primaryHref}
        size={size}
        leadingVisual={RepoForkedIcon}
        data-testid="fork-button"
        aria-label={`Fork your own copy of ${repoNameWithOwner}`}
        onClick={() =>
          sendClickAnalyticsEvent({
            category: 'REPOSITORY_OVERVIEW',
            action: 'click:fork',
            label: 'location:header;ref_cta:fork_button',
          })
        }
      >
        Fork
        <CounterLabel className="ml-1">
          <Count value={forkCount} />
        </CounterLabel>
      </Button>
      <ActionMenu
        onOpenChange={open => {
          setDropdownOpen(open)
          if (open) {
            sendClickAnalyticsEvent({
              category: 'REPOSITORY_OVERVIEW',
              action: 'click:fork_dropdown_open',
              label: 'location:header;ref_cta:fork_dropdown',
            })
          }
        }}
      >
        <ActionMenu.Anchor>
          <IconButton
            icon={TriangleDownIcon}
            size={size}
            aria-label="See your forks of this repository"
            data-testid="fork-dropdown-button"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="medium">
          <ForksList forks={forks} isLoading={isLoading} isError={isError} forkSelectPath={forkSelectPath} />
        </ActionMenu.Overlay>
      </ActionMenu>
    </ButtonGroup>
  )
}

function ForksList({
  forks,
  isLoading,
  isError,
  forkSelectPath,
}: {
  forks: ViewerFork[] | null
  isLoading: boolean
  isError: boolean
  forkSelectPath: string
}) {
  return (
    <ActionList>
      {isLoading ? (
        <ActionList.Item disabled data-testid="fork-loading">
          <Spinner size="small" />
        </ActionList.Item>
      ) : isError ? (
        <ActionList.Item disabled data-testid="fork-error">
          Forks could not be loaded
        </ActionList.Item>
      ) : forks && forks.length > 0 ? (
        forks.map(fork => (
          <ActionList.LinkItem key={fork.url} href={fork.url}>
            <ActionList.LeadingVisual>
              <GitHubAvatar src={fork.ownerAvatarUrl} size={20} />
            </ActionList.LeadingVisual>
            {fork.ownerLogin}/{fork.name}
          </ActionList.LinkItem>
        ))
      ) : (
        <ActionList.Item disabled>You don&apos;t have any forks of this repository.</ActionList.Item>
      )}
      <ActionList.Divider />
      <ActionList.LinkItem href={forkSelectPath}>
        <ActionList.LeadingVisual>
          <PlusIcon />
        </ActionList.LeadingVisual>
        Create a new fork
      </ActionList.LinkItem>
    </ActionList>
  )
}
